import { AppContext, AppContextType } from '@/AppContext'
import RecordAudioIcon from '@/CustomIcons/RecordAudioIcon'
import { RoomContext, RoomContextType } from '@/RoomContext'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { FeatureFlags } from '@/services/FeatureFlags'
import useAnalytics from '@/services/useAnalytics'
import { useCloudRecording } from '@/services/useCloudRecording'
import useRoomActions from '@/services/useRoomActions'
import useRoomContextRef from '@/services/useRoomContextRef'
import { Button } from '@/UIComponents2/Button'
import MenuButton, { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { sessionSettingsTrigger } from '../../SessionPage'
import { env } from '@/services/EnvironmentVariables'

type RecordingMenuButtonProps = {
    labelPosition?: 'inside' | 'outside'
    triggerClassName?: string
}
const RecordingMenuButton = (props: RecordingMenuButtonProps) => {
    const { t } = useTranslation()

    const { state: appState } = useContext(AppContext) as AppContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { roomStateRef } = useRoomContextRef()

    const { isCloudRecording, handleStartCloudRecording, cloudRecordingStarting, hasCloudRecordingPerms } =
        useCloudRecording()

    const { screenRecordingCommand } = useRoomActions()

    const { logUserActivityWithContext } = useAnalytics()

    const renderLocalScreenRecording = () => {
        if (!FeatureFlags.isEnabled('desktop-app-only-feature')) {
            return null
        }

        return (
            <>
                <div className="font-sm">
                    {t('Selected screen:')} {appState.appSettings.screenRecordDesktop?.label || t('none')}
                </div>
                <MenuItem asChild>
                    <Button
                        variant="rectangle"
                        className="self-stretch justify-center"
                        onClick={() => {
                            if (roomStateRef.current.isScreenRecording) {
                                screenRecordingCommand('stop')
                            } else {
                                screenRecordingCommand('start', { openSettingsOnStreamError: true })
                            }
                            logUserActivityWithContext(AnalyticsEvents.CLICKED_START_SCREEN_RECORDING_BUTTON, {
                                componentSource: 'RecordingMenuButton',
                                toggleState: !roomStateRef.current.isScreenRecording,
                            })
                        }}
                    >
                        {roomState.isScreenRecording ? t('Stop Recording') : t('Start Local Recording')}
                    </Button>
                </MenuItem>
            </>
        )
    }

    const renderCloudRecording = () => {
        if (!hasCloudRecordingPerms) {
            return null
        }
        const btnName = () => {
            if (isCloudRecording) {
                return t('Cloud Recording in Progress')
            } else if (cloudRecordingStarting) {
                return <span>{t('Starting Cloud Recording...')}</span>
            } else {
                return t('Start Cloud Recording')
            }
        }
        return (
            <Button
                variant="rectangle"
                className="text-yellow-500 self-stretch justify-center"
                onClick={() => {
                    if (!isCloudRecording && !cloudRecordingStarting) {
                        handleStartCloudRecording()
                    }
                }}
            >
                {btnName()}
            </Button>
        )
    }

    const buttonDisabled = !env.IS_ELECTRON && !hasCloudRecordingPerms

    return (
        <MenuButton
            disabled={buttonDisabled}
            tooltip={{
                text: buttonDisabled ? 'You need to download the Muse app for this feature.' : undefined,
            }}
            label={t('Record')}
            labelPosition={props.labelPosition}
            icon={<RecordAudioIcon style={{ fontSize: '1.3rem' }} />}
            triggerClassName={props.triggerClassName}
            contentClassName="p-0"
            content={
                hasCloudRecordingPerms || FeatureFlags.isEnabled('desktop-app-only-feature') ? (
                    <>
                        {renderLocalScreenRecording()}
                        {renderCloudRecording()}
                        <MenuItem asChild>
                            <Button
                                variant="rectangle"
                                className="text-yellow-500 self-stretch"
                                onClick={() => {
                                    sessionSettingsTrigger.trigger({ tab: 'recording' })
                                }}
                            >
                                {t('Session Recording Settings')}
                            </Button>
                        </MenuItem>
                    </>
                ) : (
                    <div />
                )
            }
        />
    )
}

export default RecordingMenuButton
